/**
 * Frontend error capture — ships uncaught errors, unhandled promise rejections, and React
 * crashes to the backend (`logClientError`), which logs them into Cloud Logging / Error
 * Reporting next to backend errors. Fire-and-forget, deduped + throttled so a render loop
 * can't spam logs, and it never throws back into the page.
 */
import { callFunction } from './apiClient';
import { APP_VERSION } from './buildInfo';

interface ClientError {
  message: string;
  stack?: string;
  kind: 'error' | 'unhandledrejection' | 'react';
}

const seen = new Set<string>();
let lastSentAt = 0;

export function reportClientError(e: ClientError): void {
  try {
    const key = `${e.kind}:${(e.message || '').slice(0, 200)}`;
    if (seen.has(key)) return;                 // dedupe identical errors
    if (Date.now() - lastSentAt < 1500) return; // throttle bursts
    seen.add(key);
    lastSentAt = Date.now();
    if (seen.size > 100) seen.clear();          // bound memory

    void callFunction('logClientError', {
      message: e.message,
      stack: e.stack,
      kind: e.kind,
      url: window.location.href,
      userAgent: navigator.userAgent,
      version: APP_VERSION,
    }, { requireAuth: false }).catch(() => { /* never block the app on logging */ });
  } catch { /* reporting must never throw */ }
}

let installed = false;

/** Install global error + unhandledrejection listeners (idempotent). Call once at startup. */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (ev) => {
    reportClientError({ message: ev.message || String(ev.error), stack: ev.error?.stack, kind: 'error' });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    const reason: any = ev.reason;
    reportClientError({ message: reason?.message || String(reason), stack: reason?.stack, kind: 'unhandledrejection' });
  });
}
